import { Location } from '@angular/common';
import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import type { UrlParams } from '../../types/urlParams';

import { Environment } from '../../../environments/environment';
import { FilteringService } from '../filtering';
import { ListingsService } from '../listings/listings';
import { StateService } from '../state/state';
import { Params } from './params';
import { SetURLParams } from './set-url-params';

@Injectable({
  providedIn: 'root',
})
export class UrlService {
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly filteringService = inject(FilteringService);

  private readonly _listingsService = inject(ListingsService);
  private readonly _location = inject(Location);
  private readonly _router = inject(Router);
  private readonly _stateService = inject(StateService);

  protected initialParamsPopulated = false;
  protected readonly initialParamsRead = signal(false);
  protected readonly params = computed(() => Params.call(this));
  protected readonly urlParams = signal('');

  constructor() {
    // Apply filtering params to URL.
    effect(() => {
      SetURLParams.call(this);
    });

    // Process changes to the URL params
    effect(() => {
      const _listingId = this._listingsService.selectedListing()?.id;

      if (_listingId) {
        this._location.go(`/${_listingId}`);
      } else {
        if (!this.initialParamsRead() || !this.initialParamsPopulated) {
          return;
        }

        const _paramsArray = this.urlParams()
          .split('&')
          .filter((param) => param); // Split the URL params into an array and filter out any empty strings.
        const _paramsObject: Record<string, string> = {};

        _paramsArray.forEach((param) => {
          const [_key, _value] = param.split('=');

          if (_key && _value) {
            _paramsObject[_key] = decodeURIComponent(_value);
          }
        });

        void this._router.navigate([], {
          queryParams: _paramsObject,
          queryParamsHandling: 'replace',
          relativeTo: this.activatedRoute,
        });
      }
      untracked(() => {
        if (!this.initialParamsRead()) {
          return;
        }
        if (!Environment.isApp) {
          return;
        }

        this._stateService.notifyParentOfStateChange('HF_EVENT:URL_PARAMS_CHANGED', {
          urlParams: _listingId ? `/${_listingId}` : this.urlParams(),
        });
      });
    });
  }

  public populateUrlParams(params: UrlParams): void {
    void this.filteringService.setFromUrlParams(params).then(() => {
      this.initialParamsRead.set(true);
    });
  }
}
